import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Wrench, AlertTriangle, Hammer, Building2, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HeroSection from '../components/HeroSection';
import TrustBar from '../components/TrustBar';
import AboutSection from '../components/AboutSection';
import ServicesSection from '../components/ServicesSection';
import WhoWeWorkWithSection from '../components/WhoWeWorkWithSection';
import WhyChooseUsSection from '../components/WhyChooseUsSection';
import EmergencySection from '../components/EmergencySection';
import ProjectsSection from '../components/ProjectsSection';
import FinalCTASection from '../components/FinalCTASection';
import ContactSection from '../components/ContactSection';
import Seo from '../seo/Seo';
import { buildLocalBusinessJsonLd, buildFaqJsonLd } from '../seo/siteMeta';

const homeFaqs = [
  {
    q: 'Which services does H.A.R.P Solutions LTD provide?',
    a: 'Our primary services are plumbing (including emergency plumbing), handyman services, and property maintenance across Kent, London and Essex. We also provide air conditioning and refrigeration as secondary services.'
  },
  {
    q: 'Do you cover my postcode?',
    a: 'We cover Greater London, all of Kent (including Rochester, Gillingham, Chatham, Maidstone, Dartford, Gravesend, Sittingbourne) and the major Essex towns (Romford, Brentwood, Basildon, Chelmsford, Southend). Call us if you are not sure — we travel further for larger jobs.'
  },
  {
    q: 'Are you available 24/7?',
    a: 'Yes — our emergency plumbing line is answered 24 hours a day, 365 days a year. Standard plumbing, handyman and property maintenance work is booked during normal hours.'
  },
  {
    q: 'Are your engineers insured and vetted?',
    a: 'Every engineer carries full public liability insurance, is qualified for the work they undertake, and is properly vetted before joining the team. We are happy to share credentials before any job.'
  },
];

// Curated featured-services strip pointing to the 4 priority SEO pages.
const FEATURED_SERVICES = [
  {
    to: '/services/plumbing',
    label: 'Plumbing Services',
    blurb: 'Leaks, taps, toilets, drains, kitchens, bathrooms and full re-plumbs.',
    Icon: Wrench,
  },
  {
    to: '/services/emergency-plumber',
    label: 'Emergency Plumber',
    blurb: '24/7 callouts for burst pipes, major leaks, no-heat and blocked drains.',
    Icon: AlertTriangle,
  },
  {
    to: '/services/handyman',
    label: 'Handyman Services',
    blurb: 'Flat-pack, wall-mounting, doors, sealants, carpentry and small jobs.',
    Icon: Hammer,
  },
  {
    to: '/services/property-maintenance',
    label: 'Property Maintenance',
    blurb: 'Inspections, planned care and retained contracts for landlords & agents.',
    Icon: Building2,
  },
];

const FeaturedServices = () => (
  <section className="border-y border-gray-900 bg-gray-900/30 py-12 md:py-16" id="featured-services">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Our Core Services</h2>
          <p className="text-gray-400 mt-2 max-w-2xl">
            Plumbing, emergency callouts, handyman work and property maintenance across Kent, London and Essex.
          </p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {FEATURED_SERVICES.map(({ to, label, blurb, Icon }) => (
          <Link
            key={to}
            to={to}
            data-testid={`home-featured-${to.split('/').pop()}`}
            className="group relative rounded-xl border border-gray-800 hover:border-amber-500/60 bg-gray-900/50 p-6 transition-colors"
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500/10 text-amber-500">
                <Icon className="w-5 h-5" />
              </span>
              <h3 className="text-white font-semibold group-hover:text-amber-500">{label}</h3>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">{blurb}</p>
            <span className="inline-flex items-center gap-1 text-amber-500 text-sm font-medium mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
              Read more <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const HomePage = () => {
  const { hash } = useLocation();

  // Scroll to the section anchor when arriving with a /#section URL
  // (e.g. clicking "About" from a service page).
  useEffect(() => {
    if (!hash) return undefined;
    const id = hash.replace('#', '');
    // Wait one tick for sections to mount, then scroll.
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Seo
        title="Emergency Plumber & Handyman Services in Kent, London & Essex | H.A.R.P Solutions LTD"
        description="Looking for a reliable plumber or handyman in Kent, London or Essex? H.A.R.P Solutions LTD provides emergency plumbing, leak repairs, handyman services and property maintenance. Call +44 330 122 1094."
        canonical="/"
        keywords="emergency plumber near me, local plumber, handyman near me, property maintenance services, plumbing repairs, emergency plumbing services, leak detection, home maintenance services, Kent, London, Essex"
        jsonLd={[buildLocalBusinessJsonLd(), buildFaqJsonLd(homeFaqs)]}
      />
      <Header />
      <main>
        {/* H1-style intro for SEO: the HeroSection contains the visual H1.
            We surface the SEO H1 phrasing through the page title + JSON-LD,
            and add the featured-services strip immediately below the trust bar
            so search-engine crawlers find internal links to all four priority pages. */}
        <HeroSection />
        <TrustBar />
        <FeaturedServices />
        <AboutSection />
        <ServicesSection />
        <WhoWeWorkWithSection />
        <WhyChooseUsSection />
        <EmergencySection />
        <ProjectsSection />
        <FinalCTASection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
